import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Role {
  id: string
  tenant_id: string
  name: string
  permissions: Record<string, any>
  created_at: string
}

export interface RoleFilters {
  search?: string
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedRoles {
  data: Role[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const ROLES_QUERY_KEY = 'roles'

export const useRoles = (filters: RoleFilters = {}) => {
  return useQuery({
    queryKey: [ROLES_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedRoles> => {
      const response = await apiClient.getRoles(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 100,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
  })
}

export const useRole = (id: string) => {
  return useQuery({
    queryKey: [ROLES_QUERY_KEY, id],
    queryFn: async (): Promise<Role> => {
      const response = await apiClient.get(`/api/v1/roles/${id}`)
      return response.data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { name: string; permissions: Record<string, any> }) => {
      const response = await apiClient.createRole(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
    },
  })
}

export const useUpdateRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; permissions?: Record<string, any> } }) => {
      const response = await apiClient.updateRole(id, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
    },
  })
}

export const useDeleteRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deleteRole(id)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
    },
  })
}
